import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { matchPath, useHistory, useLocation, useParams } from 'react-router-dom';

import { getOrderMortgages } from './services/queries';
import { useAccountingUpdateSocketEvent } from "hooks/useAccountingUpdateFromSocket";

const useMortgagePayoffs = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const history = useHistory();

  const queryCache = useQueryClient();
  useAccountingUpdateSocketEvent({
    callback: () => queryCache.invalidateQueries(['order-mortgages', orderId])
  });

  const getOrder = useQuery(['order-mortgages', orderId], () => getOrderMortgages(orderId), {
    enabled: !!orderId,
    refetchOnWindowFocus: false,
  });

  const firstMortgageId = getOrder.data?.getOrder?.mortgages?.[0]?._id;

  useEffect(() => {
    const payoffsPath = `/orders/details/${orderId}/payoffs`;

    if (!firstMortgageId || getOrder.isFetching) return;

    // `pathname` re-runs this on navigation; the live `history.location` is matched so a route change that
    // lands before this effect flushes is never overwritten. `replace` keeps Back off the unopened base route.
    if (matchPath(history.location.pathname, { path: payoffsPath, exact: true })) {
      history.replace(`${payoffsPath}/mortgage/${firstMortgageId}`);
    }
  }, [orderId, pathname, firstMortgageId, getOrder.isFetching, history]);

  return {
    mortgages: getOrder.data?.getOrder.mortgages,
    loadingMortgages: getOrder.isLoading || getOrder.isFetching,
  };
};

export default useMortgagePayoffs;
